import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, IconButton, Input, InputAdornment, MenuItem, Paper, Select, Slider, Stack, TextField, Typography } from "@mui/material";
import mapboxgl, { Map } from "mapbox-gl";
import { useEffect, useRef, useState } from "react";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { CheckCircle, MyLocation, NorthEastRounded, RotateLeft, RotateRight, SearchOutlined, SouthWestRounded, VisibilityOffRounded, VisibilityRounded } from "@mui/icons-material";
import { calculateRotatedCoordinates, calculateScaledCoordinates, getCoordinates, loadOverlay } from "../../../../components/RobotMap/helpers/map";
import { DEFAULT_WIDTH_GIS, OVERLAY_APPEND_KEY } from "../../../../components/RobotMap/constants";
import { COLOR } from "../../../../utils/colors";
import { shadow } from "../../../../utils/styles";
import UploadIcon from "../../../../components/icons/UploadIcon";
import { useListMap } from "../../../../hooks/map";
import { VIEW } from "../../../../utils/breakpoint";
import { smoothTransition } from "../../../../utils/animate";
import OverlaySetting from "../../../../components/RobotMap/models/OverlaySetting";
import { mockURL } from "../../../../utils/mock";
import { MapType } from "../../../../hooks/map.type";
import SearchAddress from "../../layout-maps/_form/search-address";
import { SearchAddressType } from "../../layout-maps/_form/_type";
import { Coordinates } from "../../../../components/RobotMap/RobotMap.type";
import { useConfigListRobot, useCreateRobotMapMutation, useDetailConfigRobotMap, useListBuilding, useListConfigRobotMap, useUpdateRobotMapMutation } from "../../../../hooks/config-page";
import swiftbot from "../../../../assets/swiftbot.png"
import { useQueryState } from "nuqs";
import { BUILDING_UUID_PARAM_KEY } from "../../_const";
import { Building } from "../../../../hooks/config-page/building.type";
import { UpdateRobotMapProps } from "../../../../hooks/config-page/robot-map.type";
import { showSuccessToast, toDegree, toRadian } from "../../../../utils/utils";

const overlaySetting: {
  dragging: boolean,
  startCoordinates?: number[],
  coordinates: number[][],
  opacity: number,
  rotation: number,
} = {
  dragging: false,
  startCoordinates: [],
  coordinates: [],
  opacity: 100,
  rotation: 0
}

const newMapLayerName = 'new-map-overlay'
const moreControlButtonStyle = { flex: 'none', minWidth: '36px', color: COLOR.accent.blue }

export default function EditRobotMapScreen({
  onSuccess,
  onCancel,
  robotMapId
}: {
  onSuccess?: () => void,
  onCancel?: () => void,
  robotMapId: string
}) {
  const mapContainer = useRef<HTMLElement | string>("");
  const map = useRef<Map | null>(null);
  const [zoomLevel] = useState(18)

  const inputFileRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File>()
  const [showLine, setShowLine] = useState(false)
  const [selectedOpacity, setSelectedOpacity] = useState(overlaySetting.opacity)
  const rotateIntervalRef = useRef<NodeJS.Timer | null>(null);
  const opacityIntervalRef = useRef<NodeJS.Timer | null>(null);


  const [selectedBuildingId, setSelectedBuildingId] = useQueryState(BUILDING_UUID_PARAM_KEY);

  const { data: dataListBuilding } = useListBuilding({
    page_size: 100
  })
  const listBuildings = dataListBuilding?.data || []
  const [buildingId, setBuildingId] = useState(selectedBuildingId || '')
  const selectedBuilding: Building | undefined = !!buildingId ? (listBuildings.find((building) => building.building_uuid === buildingId)) : undefined
  const levelOptions = !!selectedBuilding ? selectedBuilding.building_levels : []

  const [mapName, setMapName] = useState('')
  const [level, setLevel] = useState('')
  const [levelName, setLevelName] = useState('')
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [selectedMapId, setSelectedMapId] = useState('')
  const [selectedLevelId, setSelectedLevelId] = useState('')
  const [isNewUploadFile, setIsNewUploadFile] = useState(false)
  const [robotMac, setRobotMac] = useState('')

  const [searchRobot, setSearchRobot] = useState('')

  const [initialized, setInitialized] = useState(false)
  const [isFirstLoadImage, setIsFirstLoadImage] = useState(true)

  const { data: dataRobotMapDetail, refetch: refetchRobotMapDetail } = useDetailConfigRobotMap(robotMapId, { enabled: true })
  const robotMapData = dataRobotMapDetail?.data.map
  const buildingOfMap = dataRobotMapDetail?.data.building

  useEffect(() => {
    if (!robotMapData || !buildingOfMap) {
      return
    }

    setMapName(robotMapData.map_name)
    setBuildingId(buildingOfMap.building_uuid)
    setRobotMac(robotMapData.robot || '')

  }, [robotMapData, buildingOfMap])

  useEffect(() => {
    if (!robotMapData || !levelOptions || levelOptions.length === 0) {
      return
    }
    const findLevel = levelOptions.find((level) => level.level.toString() === robotMapData.map_level.toString());
    if (findLevel) {
      setSelectedLevelId(findLevel.id || '')
    }
  }, [robotMapData, levelOptions])

  const handleInitSelectedMap = () => {
    if (!robotMapData || !map.current || initialized) {
      return
    }


    setMapName(robotMapData.map_name)
    // setLevel(robotMapData.map_level.toString())

    if (!robotMapData.map_image) {
      return
    }

    const fileName = robotMapData.map_image.split('/').pop() || 'map-image.png';

    const request = new XMLHttpRequest();
    request.open('GET', robotMapData.map_image, true);
    request.responseType = 'blob';
    request.onload = function () {
      const reader = new FileReader();
      reader.readAsDataURL(request.response);
      reader.onload = function () {
        setSelectedFile(new File([request.response], fileName, { type: request.response.type }))
        setInitialized(true);
      };
    };
    request.send();
  }

  useEffect(() => {
    if (!robotMapData || !map.current) {
      return
    }

    // Wait Map Style loaded
    if (map.current.isStyleLoaded()) {
      handleInitSelectedMap()
    } else {
      map.current.once('style.load', handleInitSelectedMap)
    }

    return () => {
      map.current?.off('style.load', handleInitSelectedMap)
    }
  }, [robotMapData])


  const { trigger: submitUpdateRobotMap } = useUpdateRobotMapMutation(robotMapId, {
    onSuccess: () => {
      showSuccessToast('Robot Map updated successfully')
      onSuccess?.()
    }
  })

  useEffect(() => {
    if (map.current) return; // initialize map only once
    if (!mapContainer.current) return
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/hanscau/clxan4269025p01qxaxqc0ekr",
      center: [103.83484878754473, 1.2782008843142307],
      zoom: zoomLevel,
      minZoom: 10,
      maxZoom: 26,
      attributionControl: false,
      doubleClickZoom: false,
    });
    map.current.on('style.load', () => {
      if (!robotMapData) {
        return
      }
      handleInitSelectedMap()
    });

    // Remove Label
    // map.current.on("load", () => {
    //   if (map.current && map.current.isStyleLoaded()) {
    //     const layers = map.current.getStyle().layers as mapboxgl.Layer[];
    //     layers.forEach(layer => {
    //       // remove text label
    //       if (layer.type === "symbol") {
    //         map.current?.removeLayer(layer.id);
    //       }
    //     });
    //   }
    // })

    map.current.addControl(new mapboxgl.NavigationControl({ showZoom: true }));
    map.current.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true
      })
    );

    // map.current?.on('click', handleClickRef.current);

    // return () => {
    //   map.current?.remove();
    // };
  }, []);

  const onOpenFile = () => {
    if (!inputFileRef.current) {
      return
    }

    inputFileRef.current.click()
  }


  const onSubmit = () => {
    if (!map.current) return;
    if (!mapName || !buildingId || !selectedLevelId || !selectedFile) {
      return;
    }

    let coordinates = overlaySetting.coordinates
    if (overlaySetting.rotation !== 0) {
      const negateRotation = -overlaySetting.rotation
      coordinates = calculateRotatedCoordinates(overlaySetting.coordinates, negateRotation)
    }

    const form: UpdateRobotMapProps = {
      name: mapName,
      building_uuid: buildingId,
      level_id: selectedLevelId,
      map_latitude_tl: coordinates[0][1],
      map_longitude_tl: coordinates[0][0],
      map_latitude_br: coordinates[2][1],
      map_longitude_br: coordinates[2][0],
      orientation: toRadian(overlaySetting.rotation),
      robot_mac: robotMapData?.robot || '',
    }

    if (isNewUploadFile) {
      form.map_image = selectedFile;
    }

    if (selectedLevelId === 'new') {
      form.add_level_name = levelName;
      form.add_level_level = parseInt(level);
    }

    submitUpdateRobotMap(form)
  }

  useEffect(() => {
    if (!selectedFile) {
      return
    }
    const fileURL = URL.createObjectURL(selectedFile)

    const imageObj = new Image()
    imageObj.onload = (e) => {
      const height = imageObj.height
      const width = imageObj.width


      addMapOverlay(fileURL, width, height, isFirstLoadImage)
      setIsFirstLoadImage(false)
      // const assumedFirstWidthInGIS
    }
    imageObj.src = fileURL
  }, [selectedFile])

  const addMapOverlay = (imageUrl: string, widthImage: number, heightImage: number, isFirstLoad = false) => {
    if (!map.current) {
      return
    }
    const mapboxMap = map.current

    const name = newMapLayerName
    const widthGIS = DEFAULT_WIDTH_GIS
    const imageScale = widthGIS / widthImage;
    const heightGIS = heightImage * imageScale;


    let currentCoordinates: Coordinates = [[0, 0], [0, 0], [0, 0], [0, 0]];

    if (isFirstLoad) {
      const lng_tl = robotMapData?.map_longitude_tl || 0
      const lat_tl = robotMapData?.map_latitude_tl || 0
      const lng_br = robotMapData?.map_longitude_br || 0
      const lat_br = robotMapData?.map_latitude_br || 0

      const coordinates = [
        [lng_tl, lat_tl],
        [lng_br, lat_tl],
        [lng_br, lat_br],
        [lng_tl, lat_br]
      ]
      const degreeRotation = toDegree(robotMapData?.map_orientation || 0)

      const rotatedCoordinates = calculateRotatedCoordinates(coordinates, degreeRotation);

      overlaySetting.rotation = degreeRotation
      overlaySetting.coordinates = rotatedCoordinates
      currentCoordinates = rotatedCoordinates as Coordinates
    } else {
      setIsNewUploadFile(true)
      const centerCoordinate = map.current.getCenter()
      currentCoordinates = getCoordinates(centerCoordinate.lng, centerCoordinate.lat, widthGIS, heightGIS) as Coordinates
      overlaySetting.coordinates = currentCoordinates
      overlaySetting.rotation = 0
    }

    if (!map.current.getLayer(newMapLayerName)) {
      mapboxMap.addSource(newMapLayerName, {
        type: "image",
        url: imageUrl,
        coordinates: overlaySetting.coordinates as Coordinates,
      });

      mapboxMap.addLayer({
        id: newMapLayerName,
        type: "raster",
        source: newMapLayerName,
        paint: {
          "raster-fade-duration": 0,
          "raster-opacity": (selectedOpacity / 100)
        }
      });

      // TMP DISABLE MAP
      const geometryData =
      {
        type: 'geojson' as 'geojson',
        data: {
          properties: {},
          type: 'Feature' as 'Feature',
          geometry: {
            type: 'Polygon' as 'Polygon',
            coordinates: [overlaySetting.coordinates]
          }
        }
      }

      mapboxMap.addSource(newMapLayerName + OVERLAY_APPEND_KEY, geometryData)
      mapboxMap.addLayer({
        id: newMapLayerName + OVERLAY_APPEND_KEY,
        type: 'fill',
        source: newMapLayerName + OVERLAY_APPEND_KEY,
        paint: {
          "fill-color": '#1976d2',
          "fill-opacity": 0 / 100,
        }
      })
      if (showLine) {
        mapboxMap.addLayer({
          id: newMapLayerName + OVERLAY_APPEND_KEY + '-line',
          type: 'line',
          source: newMapLayerName + OVERLAY_APPEND_KEY,
          paint: {
            "line-color": '#1976d2',
            "line-opacity": 1,
          }
        })
      }
    } else {
      (mapboxMap.getSource(newMapLayerName) as mapboxgl.ImageSource).updateImage({
        url: imageUrl,
        coordinates: currentCoordinates
      });
      (mapboxMap.getSource(name + OVERLAY_APPEND_KEY) as mapboxgl.GeoJSONSource).setData({
        type: "Feature",
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [currentCoordinates],
        },
      })
    }

    mapboxMap.on("mousedown", name + OVERLAY_APPEND_KEY, (e) => {
      overlaySetting.dragging = true
      overlaySetting.startCoordinates = [e.lngLat.lng, e.lngLat.lat]
      mapboxMap.dragPan.disable()
      mapboxMap.doubleClickZoom.disable();
      e.preventDefault()
    });

    mapboxMap.on("mouseenter", name + OVERLAY_APPEND_KEY, (e) => {
      if (!overlaySetting.dragging) {
        mapboxMap.getCanvas().style.cursor = 'pointer';
      }
    })

    mapboxMap.on("mouseleave", name + OVERLAY_APPEND_KEY, () => {
      mapboxMap.getCanvas().style.cursor = '';
    })

    mapboxMap.on("mousemove", (e) => {
      mapboxMap.doubleClickZoom.disable();
      if (overlaySetting.dragging && overlaySetting.startCoordinates && overlaySetting.coordinates && overlaySetting.coordinates.length > 0) {
        e.preventDefault()

        // const coords = e.lngLat;
        mapboxMap.getCanvas().style.cursor = 'grabbing';

        // Count the drag distance
        const dx = (e.lngLat.lng - overlaySetting.startCoordinates[0]);
        const dy = (e.lngLat.lat - overlaySetting.startCoordinates[1]);

        // set the new coordinate after addition in (x & y) coordinate
        const newCoordinates = overlaySetting.coordinates.map(([lng, lat]) => [lng + dx, lat + dy]) as Coordinates;

        // geometryData.data.geometry.coordinates = [newCoordinates];
        (mapboxMap.getSource(name) as mapboxgl.ImageSource).setCoordinates(newCoordinates);
        (mapboxMap.getSource(name + OVERLAY_APPEND_KEY) as mapboxgl.GeoJSONSource).setData({
          type: "Feature",
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [newCoordinates],
          },
        })
      }
    })

    const onStopDragging = (lngLat?: number[]) => {
      mapboxMap.doubleClickZoom.enable();
      if (overlaySetting.dragging) {
        mapboxMap.dragPan.enable()
        overlaySetting.dragging = false
        if (lngLat && overlaySetting.startCoordinates && overlaySetting.coordinates) {
          const dx = (lngLat[0] - overlaySetting.startCoordinates[0]);
          const dy = (lngLat[1] - overlaySetting.startCoordinates[1]);
          // set the new coordinate after addition in (x & y) coordinate
          const newCoordinates = overlaySetting.coordinates.map(([lng, lat]) => [lng + dx, lat + dy]);
          overlaySetting.coordinates = newCoordinates;
        }
        overlaySetting.startCoordinates = undefined
        mapboxMap.getCanvas().style.cursor = '';
      }
    }

    mapboxMap.on("mouseup", name + OVERLAY_APPEND_KEY, (e) => {
      onStopDragging([e.lngLat.lng, e.lngLat.lat])
    })

    mapboxMap.on("click", (e) => {
      if (overlaySetting.dragging) {
        onStopDragging([e.lngLat.lng, e.lngLat.lat])
      }
    })
  }

  const handleFileChange = (files: FileList | undefined | null) => {
    if (!files) {
      return
    }

    const file = files[0]

    setSelectedFile(file)
  }

  const onChangeOpacity = (value: number) => {
    if (!selectedFile || !map.current || value > 100 || value < 0) {
      return
    }

    overlaySetting.opacity = value;
    setSelectedOpacity(value);
    // map.current.setPaintProperty(newMapLayerName, 'raster-opacity', value / 100)
    map.current.setPaintProperty(newMapLayerName, 'raster-opacity', value / 100)
  }

  const increaseOpacityHandler = () => {
    opacityIntervalRef.current = setInterval(() => {
      onChangeOpacity(overlaySetting.opacity + 1)
    }, 200)
  }

  const decreaseOpacityHandler = () => {
    opacityIntervalRef.current = setInterval(() => {
      onChangeOpacity(overlaySetting.opacity - 1)
    }, 200)
  }

  const removeOpacityHandler = () => {
    // if (opacityIntervalRef.current) {
    //   clearInterval(opacityIntervalRef.current)
    //   opacityIntervalRef.current = null
    // }
  }

  const onRotate = (degree: number) => {
    // If no map selected then stop
    if (!selectedFile || !map.current) {
      return
    }
    const currentRotation = (overlaySetting.rotation ? overlaySetting.rotation : degree)
    overlaySetting.rotation = currentRotation + degree

    const newCoords = calculateRotatedCoordinates(overlaySetting.coordinates, degree);
    overlaySetting.coordinates = newCoords;
    (map.current.getSource(newMapLayerName) as mapboxgl.ImageSource).setCoordinates(newCoords);
    (map.current.getSource(newMapLayerName + OVERLAY_APPEND_KEY) as mapboxgl.GeoJSONSource).setData({
      type: "Feature",
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [newCoords],
      },
    });
    // setCoordinates(selectedMapName, newCoords)
  }

  const onChangeScale = (scaleFactor: number) => {
    if (!selectedFile || !map.current) {
      return
    }

    const newCoords = calculateScaledCoordinates(overlaySetting.coordinates, scaleFactor) as Coordinates;

    overlaySetting.coordinates = newCoords;
    (map.current.getSource(newMapLayerName) as mapboxgl.ImageSource).setCoordinates(newCoords);
    (map.current.getSource(newMapLayerName + OVERLAY_APPEND_KEY) as mapboxgl.GeoJSONSource).setData({
      type: "Feature",
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [newCoords],
      },
    })
    // setCoordinates(selectedMapName, newCoords)
  };

  const onRotateLeft = () => {
    onRotate(-1)
  }

  const onRotateRight = () => {
    onRotate(1)
  }

  const rotateLeftHandler = () => {
    rotateIntervalRef.current = setInterval(() => {
      onRotateLeft()
    }, 200);
  }

  const removeRotateLeftHandler = () => {
    if (rotateIntervalRef.current) {
      clearInterval(rotateIntervalRef.current)
      rotateIntervalRef.current = null
    }
  }

  const rotateRightHandler = () => {
    rotateIntervalRef.current = setInterval(() => {
      onRotateRight()
    }, 200);
  }

  const removeRotateRightHandler = () => {
    if (rotateIntervalRef.current) {
      clearInterval(rotateIntervalRef.current)
      rotateIntervalRef.current = null
    }
  }

  const onLocateImage = () => {
    if (!selectedFile || !map.current) {
      return
    }

    const box = overlaySetting.coordinates
    map.current.fitBounds([
      [box[3][0], box[3][1]],
      [box[1][0], box[1][1]]
    ], {
      padding: 20,
      maxZoom: 18
    });
  }

  const onLocate = (data: SearchAddressType) => {
    if (!map.current) {
      return
    }

    const long = parseFloat(data.lon)
    const lat = parseFloat(data.lat)

    map.current.flyTo({
      center: [long, lat],
      zoom: 18
    });
  }

  const isDisableSubmit = () => {
    if (!mapName || !selectedLevelId || !selectedFile) {
      return true
    }
    if (selectedLevelId === 'new' && !level && !levelName) {
      return true
    }
    return false
  }

  const { data: dataRobotMaps, refetch } = useConfigListRobot({
    search: searchRobot
  })
  const listRobot = dataRobotMaps?.robots || []

  useEffect(() => {
    refetch()
  }, [searchRobot])

  return (
    <Box sx={{ maxWidth: "100%", m: "auto" }}>
      <Box sx={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
        <Box
          ref={mapContainer}
          sx={{
            position: 'absolute',
            inset: 0,
            width: "100%",
            height: "100%",
            // zIndex: 0,
          }}
        ></Box>

        {selectedFile && (
          <Box sx={{
            position: 'absolute',
            bottom: '28px',
            insetInline: '16px',
            maxWidth: '480px',
            width: '100%',
            minHeight: '48px',
            backgroundColor: COLOR.white,
            mx: 'auto',
            ...shadow,
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '16px',
            padding: '8px',
            justifyContent: 'center',
            userSelect: 'none'
          }}>
            <Stack spacing={2} direction="row" sx={{ alignItems: 'center' }}>
              <IconButton
                onClick={() => onChangeOpacity(overlaySetting.opacity - 1)}
                onMouseDown={decreaseOpacityHandler}
                onMouseUp={removeOpacityHandler}
                onMouseLeave={removeOpacityHandler}
              >
                <VisibilityOffRounded sx={{ opacity: 0.3 }} />
              </IconButton>
              <Slider
                min={0}
                max={100}
                value={selectedOpacity}
                onChange={(_, value) => onChangeOpacity(value as number)}
                sx={{ minWidth: '64px' }}
              />
              <IconButton
                onMouseDown={increaseOpacityHandler}
                onMouseUp={removeOpacityHandler}
                onMouseLeave={removeOpacityHandler}
                onClick={() => onChangeOpacity(overlaySetting.opacity + 1)}
              >
                <VisibilityRounded />
              </IconButton>
            </Stack>
            <IconButton
              onMouseDown={rotateLeftHandler}
              onMouseUp={removeRotateLeftHandler}
              onMouseLeave={removeRotateLeftHandler}
              onClick={onRotateLeft}
              sx={moreControlButtonStyle}
            >
              <RotateLeft />
            </IconButton>
            <IconButton
              onMouseDown={rotateRightHandler}
              onMouseUp={removeRotateRightHandler}
              onMouseLeave={removeRotateRightHandler}
              onClick={onRotateRight}
              sx={moreControlButtonStyle}
            >
              <RotateRight />
            </IconButton>
            <IconButton
              onClick={() => onChangeScale(0.9)}
              sx={moreControlButtonStyle}
            >
              <SouthWestRounded />
            </IconButton>
            <IconButton
              onClick={() => onChangeScale(1.1)}
              sx={moreControlButtonStyle}
            >
              <NorthEastRounded />
            </IconButton>
            <IconButton
              onClick={() => onLocateImage()}
              sx={moreControlButtonStyle}
            >
              <MyLocation />
            </IconButton>
          </Box>
        )}

        <Box sx={{ position: 'absolute', right: '49px', top: '10px', display: 'flex', flexDirection: 'column', borderRadius: '10px' }}>
          <Accordion sx={{ width: '400px' }} defaultExpanded>
            <AccordionSummary
              expandIcon={<GridExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography component="span" fontWeight={600}>Robot Map Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', }}>
                  <Typography sx={{ width: '100px', flex: 'none' }}>Robot:</Typography>
                  <Select
                    size='small'
                    disabled={true}
                    displayEmpty
                    sx={{ width: '100%', flex: 'auto' }}
                    value={robotMac}
                    onChange={(e) => {
                      setRobotMac(e.target.value)
                    }}
                    renderValue={(value: string) => {
                      if (!value || value.length === 0) {
                        return ""
                      }
                      const findRobot = listRobot.find((robot) => robot.robot_mac === value);
                      return findRobot ? findRobot.robot_name : '';
                    }}
                  >
                    {listRobot.length === 0 && (
                      <MenuItem value={robotMac}>
                        <em>No Robot Options</em>
                      </MenuItem>
                    )}
                    {listRobot.map((robot) => (
                      <MenuItem key={robot.robot_mac} value={robot.robot_mac}>
                        {robot.robot_name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', }}>
                  <Typography sx={{ width: '100px', flex: 'none' }}>Map Name:</Typography>
                  <TextField value={mapName} onChange={(e) => setMapName(e.target.value)} size="small" sx={{ width: '100%' }}></TextField>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', }}>
                  <Typography sx={{ width: '100px', flex: 'none' }}>Building: </Typography>
                  <Select
                    size='small'
                    disabled={!!selectedBuildingId}
                    // displayEmpty
                    value={buildingId}
                    onChange={(e) => setBuildingId(e.target.value)}
                    sx={{ width: '100%', flex: 'auto' }}
                    renderValue={(value) => {
                      if (!value) {
                        return ""
                      }
                      return listBuildings.find((b) => b.building_uuid === value)?.building_name
                    }}
                  >
                    {listBuildings.length > 0 && listBuildings.map((building, index) => (
                      <MenuItem
                        key={index}
                        value={building.building_uuid}
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {building.building_name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', }}>
                  <Typography sx={{ width: '100px', flex: 'none' }}>Map Level:</Typography>
                  <Select
                    size='small'
                    displayEmpty
                    sx={{ width: '100%', flex: 'auto' }}
                    value={selectedLevelId}
                    onChange={(e) => {
                      if (e.target.value !== 'new') {
                        setLevel('')
                        setLevelName('')
                      }
                      setSelectedLevelId(e.target.value)
                    }}
                    renderValue={(value: string) => {
                      if (levelOptions.length === 0 || !buildingId) {
                        return <em>Select Building First</em>
                      }
                      if (!value || value.length === 0) {
                        return ""
                      }
                      if (value === 'new') {
                        return <em>Add New Level</em>
                      }
                      const findLevel = levelOptions.find((level) => level.id?.toString() === value.toString());
                      return findLevel ? findLevel.level_name + ' (' + findLevel.level + ')' : '';
                    }}
                  >
                    {levelOptions.length === 0 && !buildingId && (
                      <MenuItem value="">
                        <em>Select Building First</em>
                      </MenuItem>
                    )}
                    {levelOptions.map((level) => (
                      <MenuItem key={level.id} value={level.id}>
                        {level.level_name} ({level.level})
                      </MenuItem>
                    ))}
                    {levelOptions.length > 0 && (
                      <MenuItem key={'new'} value={'new'}>
                        <em>Add New Level</em>
                      </MenuItem>
                    )}
                  </Select>
                </Box>
                {selectedLevelId === 'new' && (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center' }}>
                      <TextField
                        label="Level Name"
                        value={levelName}
                        onChange={(e) => setLevelName(e.target.value)}
                        size="small"
                        sx={{ width: '100%' }}
                      />
                      <TextField
                        value={level}
                        label="Level (e.g. 1, 2, -1)"
                        onChange={(e) => setLevel(e.target.value)}
                        size="small"
                        sx={{ width: '100%' }}
                        type="number"
                      />
                    </Box>
                  </>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mt: '0px' }}>
                  <Typography sx={{ width: '10  5px', flex: 'none' }}>Map Image</Typography>
                  <Box onClick={() => onOpenFile()} sx={{ cursor: 'pointer', border: `1px dashed ${COLOR.gray[400]}`, padding: '10px', mt: '2px', borderRadius: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {!selectedFile && (
                      <>
                        <UploadIcon />
                      </>
                    )}
                    {selectedFile && (
                      <>
                        <img src={URL.createObjectURL(selectedFile)} style={{ height: '80px', objectFit: 'scale-down' }} />
                      </>
                    )}
                    <Typography sx={{ fontSize: '12px', color: COLOR.gray[400], mt: '8px' }}>Click on the button below to upload an image!</Typography>
                    <Typography sx={{ fontSize: '12px', color: COLOR.gray[400] }}>Supported formats: .jpg, .jpeg, .png</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        mt: '8px',
                        borderRadius: '999px',
                        paddingInline: '24px',
                        paddingTop: '4px',
                        paddingBottom: '4px'
                      }}>Upload</Button>
                  </Box>
                  <input hidden type="file" ref={inputFileRef} onChange={(e) => handleFileChange(e.target.files)} accept="image/jpg, image/png, image/jpeg" />
                </Box>
                <Box>
                  <Button onClick={onSubmit} disableElevation disabled={isDisableSubmit()} variant="contained" sx={{ width: '100%' }}>
                    Update Robot Map
                  </Button>
                </Box>
                <Box>
                  <Button onClick={() => onCancel?.()} disableElevation variant="outlined" sx={{ width: '100%' }}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ position: 'absolute', overflow: 'hidden', ...shadow, left: '16px', top: '10px', display: 'flex', flexDirection: 'column', borderRadius: '10px' }}>
          <Box sx={{ display: 'flex', bgcolor: 'white', flexDirection: 'column', padding: '12px' }}>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 600,
                color: COLOR.neutral[900]
              }}
            >
              Create Robot Map
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: COLOR.neutral[900],
                opacity: 0.6,
              }}
            >
              Fill the form to create a robot map
            </Typography>
          </Box>
          <SearchAddress
            sx={{
              paddingTop: '4px',
            }}
            onSelect={(result) => {
              onLocate(result)
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}